// Remove Vietnamese accents for consistent comparison
const removeVietnameseAccent = (str) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
};

// Calculate Levenshtein distance (Edit Distance)
const levenshteinDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  // Increment along the first column of each row
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // Increment each column in the first row
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          Math.min(
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1, // deletion
          ),
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

/**
 * Fuzzy match function
 * @param {string} text - The text to search within (e.g. "Trà Sữa Đào")
 * @param {string} search - The search term (e.g. "tra dao" or "dưa gagn")
 * @returns {boolean}
 */
export const fuzzyMatch = (text, search) => {
  if (!search) return true;
  if (!text) return false;

  // 0. Pre-processing
  const normalizedText = removeVietnameseAccent(text).toLowerCase();
  const normalizedSearch = removeVietnameseAccent(search).toLowerCase();

  // 1. Direct substring check (Fastest & most common)
  if (normalizedText.includes(normalizedSearch)) return true;

  // 2. Token-based matching (handles "tra dao" -> "Tra Sua Dao")
  const searchTokens = normalizedSearch
    .trim()
    .split(/\s+/)
    .filter((t) => t);

  // If we have multiple tokens, check if ALL tokens match SOME part of the text
  // We allow fuzzy matching for EACH token against the words in the text
  const textWords = normalizedText.split(/\s+/).filter((w) => w);

  return searchTokens.every((token) => {
    // A. Direct Token Match
    if (normalizedText.includes(token)) return true;

    // B. Fuzzy Token Match
    // "gagn" vs "gang" -> distance 2 (swap) or 1 (transposition if supported)
    // With simple Levenshtein, "gagn" vs "gang" is distance 2.

    // Allowable errors based on token length:
    // Length < 3: 0 errors (strict)
    // Length 3-5: 1 error
    // Length > 5: 2 errors
    let maxErrors = 0;
    if (token.length > 5) maxErrors = 2;
    else if (token.length >= 3) maxErrors = 1;

    // Check against every word in the text
    return textWords.some((word) => {
      // Check direct match
      if (word.includes(token)) return true;

      // Check approximate match
      // Optimization: if length difference is greater than maxErrors, skip
      if (Math.abs(word.length - token.length) > maxErrors) return false;

      // Allow matching "gagn" with "gang" (len 4, maxErr 1) -> Wait, 4 chars gets 1 error.
      // "gagn" vs "gang" is 2 substitutions => fails.
      // We need to be slightly more lenient for transpositions or increase logic.
      // Let's bump tolerance for short words slightly if it looks like a transposition?
      // Actually, let's relax rule: Length >= 4 allows 2 errors.
      if (token.length >= 4) maxErrors = 2;

      const dist = levenshteinDistance(word, token);
      return dist <= maxErrors;
    });
  });
};
